/* eslint-disable indent */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/styles';
import { Card, CardContent, Button, Grid, Typography, CircularProgress } from '@material-ui/core';
import { FieldDropdown, DeviceDropdown, MenuWithId, PropertyDropdown } from 'components';
import { useOrganizations, useDevices, useTelemetry, useProperties } from 'hooks';
import { TimePeriods } from 'utils/ReadingsHelpers';
import { red } from '@mui/material/colors';
import _ from 'lodash';
import { DashboardHelpers } from 'utils';

const useStyles = makeStyles((theme) => ({
  root: { overflow: 'visible' },
  alert: {
    marginBottom: theme.spacing(3),
  },
  formGroup: {
    marginBottom: theme.spacing(3),
    width: '100%',
    flexGrow: 1,
  },
  formGroupGrid: {
    marginBottom: theme.spacing(3),
    flexGrow: 1,
  },
  fieldGroup: {
    display: 'flex',
    alignItems: 'center',
  },
  fieldHint: {
    margin: theme.spacing(1, 0),
  },
  addButton: {
    marginLeft: theme.spacing(1),
  },
  tags: {
    marginTop: theme.spacing(1),
    '& > * + *': {
      marginLeft: theme.spacing(1),
    },
  },
  flexGrow: {
    flexGrow: 1,
  },
  modal: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '27%',
    [theme.breakpoints.down('md')]: {
      width: '80%',
    },
    bgcolor: 'background.white',
    boxShadow: 24,
  },
  modalActionCancel: {
    marginLeft: '2%',
  },
  modalActionPrimary: {
    marginLeft: '5%',
  },
  helperTextLong: { fontSize: 10, marginBottom: 10, color: red[600] },
  helperText: { fontSize: 12, marginBottom: 10, color: red[600] },
}));

const DataExportModalForm = ({ passedProperty, passedField, passedDevice, hide }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { selectedOrganization } = useOrganizations();
  const { properties } = useProperties();
  const { devices } = useDevices();
  const { isFetching, exportTelemetry } = useTelemetry();

  const [localSelectedField, setLocalSelectedField] = useState(
    passedField
      ? passedField
      : {
          id: DashboardHelpers.StatusCodes.NoFieldSelected,
          name: t('none'),
        }
  );
  const [localSelectedDevice, setLocalSelectedDevice] = useState(
    passedDevice
      ? passedDevice
      : {
          id: DashboardHelpers.StatusCodes.NoDeviceSelected,
          name: t('field_average'),
        }
  );
  const [localSelectedProperty, setLocalSelectedProperty] = useState(
    passedProperty
      ? passedProperty
      : {
          id: DashboardHelpers.StatusCodes.NoPropertySelected,
          name: t('none'),
        }
  );
  const [timePeriod, setTimePeriod] = useState();

  const selectProperty = (property) => {
    if (property.id !== localSelectedProperty.id) {
      setLocalSelectedField({ id: DashboardHelpers.StatusCodes.NoFieldSelected, name: t('none_selected') }); // Reset field if property is changed
      setLocalSelectedProperty(property);
    }
  };

  const selectField = (field) => {
    if (field.id !== DashboardHelpers.StatusCodes.NoFieldSelected) {
      if (localSelectedProperty.id === DashboardHelpers.StatusCodes.NoPropertySelected) {
        const thisSelectedProperty = _.find(properties, { id: field.property_id });
        selectProperty(thisSelectedProperty);
      }
      setLocalSelectedField(field);
    } else {
      setLocalSelectedField({ id: DashboardHelpers.StatusCodes.NoFieldSelected, name: t('none_selected') });
    }
  };

  let filteredDevices = [{ id: DashboardHelpers.StatusCodes.NoDeviceSelected, name: t('field_average') }];
  if (localSelectedField) {
    filteredDevices = [...filteredDevices, ..._.filter(devices, { field_id: localSelectedField.id })];
  } else {
    filteredDevices = [...filteredDevices, ..._.filter(devices, { organization_id: selectedOrganization.id })];
  }

  const handleSubmit = async () => {
    const res = await exportTelemetry(selectedOrganization, localSelectedField, localSelectedDevice, timePeriod?.value);
    const csv = DashboardHelpers.jsonToCSV(res.payload?.telemetry);

    let fileName = selectedOrganization.name;
    if (localSelectedField?.id !== DashboardHelpers.StatusCodes.NoFieldSelected) {
      fileName = `${fileName}_${localSelectedField.name}`;
    }
    if (localSelectedDevice?.id !== DashboardHelpers.StatusCodes.NoDeviceSelected) {
      fileName = `${fileName}_${DashboardHelpers.trimHWAddress(localSelectedDevice.hw_address).replace(/:/g, '')}`;
    }
    const timeStamp = new Date();
    DashboardHelpers.exportData(
      csv,
      `${fileName}_${timeStamp.toLocaleDateString('en-US', { day: 'numeric', month: 'numeric', year: 'numeric' })}.csv`
    );
  };

  return (
    <Card className={classes.modal}>
      <CardContent>
        <form
          onSubmit={() => {
            handleSubmit();
          }}
        >
          <Grid container spacing={3} alignItems="stretch">
            <Grid item xs={12} sm={12}>
              <Typography variant="h3">{t('data_export')}</Typography>
            </Grid>
            <Grid item xs={12} sm={5}>
              <PropertyDropdown
                setSelectedProperty={selectProperty}
                selectedProperty={localSelectedProperty}
                selectedOrganization={selectedOrganization}
                properties={properties}
                disabled={isFetching}
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <FieldDropdown
                required
                setSelectedField={selectField}
                selectedField={localSelectedField}
                selectedProperty={localSelectedProperty}
                selectedOrganization={selectedOrganization}
                disabled={isFetching}
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <DeviceDropdown
                devices={filteredDevices}
                selectedField={localSelectedField}
                selectedDevice={localSelectedDevice}
                setSelectedDevice={setLocalSelectedDevice}
                disabled={isFetching}
              />
            </Grid>
            <Grid container style={{ paddingBottom: 50 }} spacing={3} item xs={12} sm={5}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" gutterBottom component="div" justifySelf="flex-start">
                  {t('time_period')} *
                </Typography>
                <MenuWithId
                  placeholder="Time Period"
                  menuItems={[
                    { id: TimePeriods.OneDay, value: TimePeriods.OneDay, name: t('one_day_full') },
                    { id: TimePeriods.OneWeek, value: TimePeriods.OneWeek, name: t('one_week_full') },
                    { id: TimePeriods.OneMonth, value: TimePeriods.OneMonth, name: t('one_month_full') },
                    { id: TimePeriods.ThreeMonth, value: TimePeriods.ThreeMonth, name: t('three_months_full') },
                    { id: TimePeriods.OneYear, value: TimePeriods.OneYear, name: t('one_year_full') },
                    { id: TimePeriods.TwentyYears, value: TimePeriods.TwentyYears, name: t('lifetime_full') },
                  ]}
                  selectedItem={timePeriod?.name}
                  selectItem={(item) => {
                    setTimePeriod(item);
                  }}
                  inputProps={{
                    maxLength: 24,
                  }}
                  size="normal"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                {isFetching && (
                  <div>
                    <CircularProgress color="primary" size={60} style={{ marginLeft: 20 }} />
                    <Typography variant="subtitle2" gutterBottom component="div">
                      {t('requesting_data')}
                    </Typography>
                  </div>
                )}
              </Grid>
            </Grid>
          </Grid>
          <Button onClick={hide} color="primary" disabled={isFetching} className={classes.modalActionCancel}>
            {t('cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
            disabled={
              isFetching || localSelectedField.id === DashboardHelpers.StatusCodes.NoFieldSelected || !timePeriod
            }
            className={classes.modalActionPrimary}
          >
            {t('submit')}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

DataExportModalForm.propTypes = {
  passedDevice: PropTypes.object,
  passedProperty: PropTypes.object,
  passedField: PropTypes.object,
  device: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  submitRequest: PropTypes.func,
  hide: PropTypes.func.isRequired,
};

DataExportModalForm.defaultProps = {
  passedDevice: undefined,
  passedProperty: undefined,
  passedField: undefined,
  device: {},
  fields: {},
};

export default DataExportModalForm;
